import { Box, Container, Heading, Text, Flex } from "@chakra-ui/react";
import { Link as RouterLink } from "react-router-dom";
import type { HeroSlide } from "../types";
import Carousel from "./Carousel";
import { ShutterMark } from "./ProductCard";

const isInternalLink = (href: string) => href.startsWith("/");

const HeroCarousel = ({ slides }: { slides: HeroSlide[] }) => {
  if (slides.length === 0) {
    return (
      <Box as="section" borderBottom="1px solid" borderColor="rs.border">
        <Container maxW="container.xl" px={{ base: 4, md: 6 }}>
          <Flex minH="360px" align="center" justify="center">
            <Box maxW="180px">
              <ShutterMark />
            </Box>
          </Flex>
        </Container>
      </Box>
    );
  }

  return (
    <Box as="section" borderBottom="1px solid" borderColor="rs.border">
      <Carousel
        showDots={slides.length > 1}
        showArrows={slides.length > 1}
        autoplayMs={slides.length > 1 ? 6000 : undefined}
        options={{ loop: true }}
      >
        {slides.map((slide) => (
          <Box
            key={slide.id}
            flex="0 0 100%"
            minW={0}
            position="relative"
            minH={{ base: "360px", md: "460px" }}
            bg="rs.bgAlt"
          >
            <Box
              position="absolute"
              inset={0}
              backgroundImage={`url(${slide.imageUrl})`}
              backgroundSize="cover"
              backgroundPosition="center"
            />
            <Box
              position="absolute"
              inset={0}
              bg="linear-gradient(90deg, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.15) 55%, rgba(0,0,0,0) 100%)"
            />
            <Container
              maxW="container.xl"
              px={{ base: 4, md: 6 }}
              h="100%"
              position="relative"
            >
              <Flex
                direction="column"
                justify="center"
                h="100%"
                maxW="480px"
                py={{ base: 12, md: 0 }}
              >
                <Heading
                  as="h1"
                  fontFamily="'DM Serif Display', serif"
                  fontSize={{ base: "32px", md: "46px" }}
                  fontWeight="400"
                  lineHeight="1.15"
                  color="white"
                  mb={4}
                >
                  {slide.headline}
                </Heading>
                {slide.subtext && (
                  <Text fontSize="14px" color="whiteAlpha.900" mb={7}>
                    {slide.subtext}
                  </Text>
                )}
                {slide.ctaLabel && slide.ctaLink && (
                  isInternalLink(slide.ctaLink) ? (
                    <Box
                      as={RouterLink}
                      to={slide.ctaLink}
                      alignSelf="flex-start"
                      bg="rs.red"
                      color="white"
                      fontSize="12.5px"
                      fontWeight="500"
                      letterSpacing="0.03em"
                      px={6}
                      py="10px"
                      borderRadius="6px"
                      transition="background 0.15s"
                      _hover={{ bg: "rs.redHover", textDecoration: "none" }}
                    >
                      {slide.ctaLabel}
                    </Box>
                  ) : (
                    <Box
                      as="a"
                      href={slide.ctaLink}
                      alignSelf="flex-start"
                      bg="rs.red"
                      color="white"
                      fontSize="12.5px"
                      fontWeight="500"
                      letterSpacing="0.03em"
                      px={6}
                      py="10px"
                      borderRadius="6px"
                      transition="background 0.15s"
                      _hover={{ bg: "rs.redHover", textDecoration: "none" }}
                    >
                      {slide.ctaLabel}
                    </Box>
                  )
                )}
              </Flex>
            </Container>
          </Box>
        ))}
      </Carousel>
    </Box>
  );
};

export default HeroCarousel;
